import { InputAdornment, TextField } from "@mui/material";
import { IOptionalField } from "@interfaces";
import { useEffect, useRef, useState } from "react";
import _ from "lodash";
import { theme } from "@utilities";
import { Dropdown } from "src/Module/CustomDropdown";
import { calculateAgeFromDob } from "src/Utilities/ageHelper";

interface IOptionalDataFieldProps {
    optionalField: IOptionalField;
    onChange: (value: any) => void;
    value: any | null;
    traveller: any | null;
    countriesData: any[];
    autoFocus?: boolean;
}

export const OptionalDataField = (props: IOptionalDataFieldProps) => {
    const { optionalField, onChange, value, traveller, countriesData, autoFocus } = props;
    const hasSetInitialValue = useRef(false);

    const travellerData = traveller?.[0];
    const details = travellerData?.is_primary ? travellerData?.customer : travellerData?.guest;
    const getCountryName = (targetCode: string | undefined) =>
        _.get(_.find(countriesData, { countryCode: targetCode }), "name", "Not Found");

    // --- Local phone edit state & last valid value (hooks must be at top level) ---
    const initialPhone = String(value ?? details?.phone_number ?? travellerData?.phone_number ?? "");
    const prevValidRef = useRef<string>(initialPhone);
    const [localValue, setLocalValue] = useState<string>(initialPhone);

    // keep localValue / prevValidRef in sync when parent value or traveller phone changes
    useEffect(() => {
        const current = String(value ?? details?.phone_number ?? travellerData?.phone_number ?? "");
        const digits = (current.match(/\d/g) || []).length;
        if (digits === 0 || digits >= 4) {
            prevValidRef.current = current;
            setLocalValue(current);
        }
    }, [value, details?.phone_number, travellerData?.phone_number]);

    useEffect(() => {
        const travellerDataFirst = traveller?.[0];
        if (travellerDataFirst) {
            initializeField(travellerDataFirst, optionalField.commonName);
        }
    }, [optionalField.commonName, optionalField.options, traveller, countriesData]);

    const initializeField = (travellerData: any, commonName: string) => {
        if (hasSetInitialValue.current || _.isEmpty(travellerData)) return;

        switch (commonName) {
            case "Email": {
                const email = details?.email ?? travellerData.email ?? details?.optional_fields?.Email;
                if (email) {
                    setTimeout(() => {
                        onChange(email);
                        hasSetInitialValue.current = true;
                    }, 80);
                }
                break;
            }

            case "Mobile": {
                const phone = details?.phone_number ?? travellerData.phone_number;
                if (phone) {
                    setTimeout(() => {
                        onChange(phone);
                        hasSetInitialValue.current = true;
                    }, 80);
                }
                break;
            }

            case "Postcode": {
                const postcode = details?.post_code ?? travellerData.postal_code;
                if (postcode) {
                    onChange(postcode);
                    hasSetInitialValue.current = true;
                }
                break;
            }

            case "Country": {
                const countryCode = details?.country_code ?? travellerData.country_code;
                if (countryCode && !_.isEmpty(countriesData)) {
                    const countryName = getCountryName(countryCode);
                    if (countryName !== "Not Found") {
                        setTimeout(() => {
                            onChange(countryName);
                            hasSetInitialValue.current = true;
                        }, 60);
                    }
                }
                break;
            }

            case "Age": {
                const dob = details?.date_of_birth ?? travellerData.date_of_birth;
                if (dob) {
                    const age = calculateAgeFromDob(dob);
                    if (age !== null) {
                        setTimeout(() => {
                            onChange(age);
                            hasSetInitialValue.current = true;
                        }, 100);
                    }
                }
                break;
            }

            case "Gender": {
                const title = travellerData.title;
                const options = optionalField.options;
                if (title && !_.isEmpty(options)) {
                    const genderMap: Record<string, string> = {
                        Mr: "Male",
                        Master: "Male",
                        Mrs: "Female",
                        Miss: "Female",
                        Ms: "Female",
                    };
                    const mapped = genderMap[title] || "Not Given";
                    const exactValue =
                        _.find(
                            options,
                            (o) =>
                                o.localeCompare(mapped, undefined, { sensitivity: "accent" }) === 0,
                        ) || "Not Given";

                    setTimeout(() => {
                        onChange(exactValue);
                        hasSetInitialValue.current = true;
                    }, 200);
                }
                break;
            }

            default: {
                // Handle any optional field that has a stored value in optional_fields
                const value = details?.optional_fields?.[commonName];
                if (value) {
                    onChange(value);
                    hasSetInitialValue.current = true;
                }
                break;
            }
        }
    };

    // --- Mobile specific UI/logic ---
    if (optionalField.commonName === "Mobile") {
        // helper to strip everything but digits, +, (), -, and space
        const sanitize = (str: string) => str.replace(/[^0-9()+\-\s]/g, "");

        const digitCountLocal = (localValue.match(/\d/g) || []).length;
        const isTooShort = digitCountLocal > 0 && digitCountLocal < 4;

        // allow ctrl/cmd shortcuts + navigation keys, otherwise only our allowed chars
        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.ctrlKey || e.metaKey) return;
            if (["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) return;
            if (!/[\d()+\-\s]/.test(e.key)) e.preventDefault();
        };

        // update local on every keystroke, commit to parent only when valid (0 or >=4 digits)
        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const next = sanitize(e.target.value);
            setLocalValue(next);

            const digits = (next.match(/\d/g) || []).length;
            if (digits === 0 || digits >= 4) {
                onChange(next);
                prevValidRef.current = next;
            }
            // if 1-3 digits, do NOT call onChange yet — let the user finish typing
        };

        // paste: sanitize then apply same commit rules
        const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
            window.requestAnimationFrame(() => {
                const pastedRaw = (e.target as HTMLInputElement).value;
                const pasted = sanitize(pastedRaw);
                setLocalValue(pasted);

                const digits = (pasted.match(/\d/g) || []).length;
                if (digits === 0 || digits >= 4) {
                    onChange(pasted);
                    prevValidRef.current = pasted;
                }
            });
        };

        // on blur: if too-short (1-3 digits) revert to travellerData.phone_number if available,
        // otherwise revert to last known valid value.
        const handleBlur = () => {
            const digits = (localValue.match(/\d/g) || []).length;
            if (digits === 0 || digits >= 4) {
                onChange(localValue);
                prevValidRef.current = localValue;
                return;
            }

            // too short -> revert to details.phone_number or travellerData.phone_number if present, else prevValidRef
            const fallback = details?.phone_number ?? travellerData?.phone_number ?? prevValidRef.current;
            setLocalValue(fallback);
            onChange(fallback);
            prevValidRef.current = fallback;
        };

        return (
            <TextField
                type="tel"
                placeholder="+1 (234) 567-8900"
                fullWidth
                value={localValue}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                error={isTooShort}
                helperText={isTooShort ? "Please enter at least 4 digits." : ""}
                sx={{
                    "& .MuiOutlinedInput-root": {
                        height: "55px",
                        "& fieldset": {
                            borderColor: theme.colors.highlight,
                        },
                        "&:hover fieldset": {
                            borderColor: `${theme.colors.highlight} !important`,
                        },
                        "&.Mui-focused fieldset": {
                            borderColor: `${theme.colors.highlight} !important`,
                        },
                        paddingTop: "20px",
                        paddingLeft: "20px",
                    },
                    "& .MuiInputBase-input": {
                        paddingTop: "12px",
                    },
                }}
                slotProps={{
                    input: {
                        inputProps: {
                            pattern: `(?=(?:.*\\d){4,})[0-9()+\\-\\s]+`,
                            title: "Enter at least 4 digits; only digits, spaces, (), +, - allowed",
                            inputMode: "tel",
                            onPaste: handlePaste,
                        },
                        startAdornment: (
                            <InputAdornment
                                position="start"
                                sx={{
                                    position: "absolute",
                                    top: 4,
                                    left: 20,
                                    pointerEvents: "none",
                                    color: "gray",
                                    fontWeight: 200,
                                    fontSize: "0.65rem",
                                    "& .MuiTypography-root": {
                                        fontSize: "0.80rem",
                                    },
                                }}
                            >
                                Phone Number
                            </InputAdornment>
                        ),
                    },
                }}
            />
        );
    }

    // --- other field types ---
    switch (optionalField.type) {
        case "text":
            return (
                <TextField
                    placeholder={optionalField.title ?? ""}
                    fullWidth
                    autoFocus={autoFocus}
                    sx={{
                        fontWeight: "bold",
                        "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                                borderColor: theme.colors.highlight,
                            },
                            "&:hover fieldset": {
                                borderColor: `${theme.colors.highlight} !important`,
                            },
                            "&.Mui-focused fieldset": {
                                borderColor: `${theme.colors.highlight} !important`,
                            },
                        },
                    }}
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                />
            );

        case "select":
            return (
                <Dropdown
                    options={optionalField.options.map((option) => ({
                        label: option,
                        value: option,
                    }))}
                    value={value}
                    placeholder={optionalField.title ?? ""}
                    onChange={(value) => onChange(value)}
                />
            );

        case "number":
            return (
                <TextField
                    type="number"
                    placeholder={optionalField.title ?? ""}
                    title={optionalField.title ?? ""}
                    fullWidth
                    autoFocus={autoFocus}
                    sx={{
                        fontWeight: "bold",
                        "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                                borderColor: theme.colors.highlight,
                            },
                            "&:hover fieldset": {
                                borderColor: `${theme.colors.highlight} !important`,
                            },
                            "&.Mui-focused fieldset": {
                                borderColor: `${theme.colors.highlight} !important`,
                            },
                        },
                    }}
                    slotProps={{ htmlInput: { min: 0 } }}
                    value={value ?? ""}
                    onChange={(e) =>
                        onChange(e.target.value === "" ? "" : Number(e.target.value))
                    }
                />
            );

        default:
            return null;
    }
};
